// ==========================================
// api.js - PHP Backend API Ä°stemcisi
// Her endpoint ayrÄ± bir PHP dosyasÄ±na POST ile istek atar.
// ==========================================

export const SITE_BASE = "https://rezervioo.com";
export const API_BASE = `${SITE_BASE}/api`;
export const BUSINESS_DATAS_BASE = `${SITE_BASE}/business_datas`;

/**
 * Ä°ÅŸletme logosu URL'si Ã¼retir.
 * cacheKey verilirse URL'e ?v=... ekleyerek cache kÄ±rar.
 * @param {number|string} businessId
 * @param {number|string|null} cacheKey
 * @returns {string}
 */
export function getBusinessLogoUrl(businessId, cacheKey = null) {
  if (!businessId) return "";
  const base = `${BUSINESS_DATAS_BASE}/${businessId}/logo/logo.png`;
  if (cacheKey === null || cacheKey === undefined || cacheKey === "") {
    return base;
  }
  return `${base}?v=${encodeURIComponent(String(cacheKey))}`;
}

/**
 * Genel fetch wrapper â€” her zaman POST ile body gÃ¶nderir.
 * @param {string} endpoint - PHP dosya adÄ± (Ã¶rn: "getBusinesses.php")
 * @param {Object} body - PHP'ye gÃ¶nderilecek veri objesi (opsiyonel)
 * @returns {Promise<any>} API'den dÃ¶nen data
 */
async function apiFetch(endpoint, body = {}) {
  try {
    const response = await fetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `HTTP ${response.status} hatasÄ±`);
    }

    return data.data;
  } catch (error) {
    console.error(`API HatasÄ± [${endpoint}]:`, error.message);
    throw error;
  }
}

/**
 * localStorage'dan auth_user_id'yi alÄ±r.
 * Admin-only endpointlerde otomatik olarak gÃ¶nderilir.
 * @returns {number|null}
 */
function getAuthUserId() {
  try {
    const stored = localStorage.getItem("yerimhazir_user");
    if (stored) {
      const user = JSON.parse(stored);
      return user.id || null;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Kimlik doÄŸrulama gerektiren API Ã§aÄŸrÄ±sÄ±.
 * auth_user_id otomatik eklenir.
 */
async function authenticatedFetch(endpoint, body = {}) {
  const authUserId = getAuthUserId();
  if (!authUserId) {
    throw new Error("GiriÅŸ yapmanÄ±z gerekiyor.");
  }
  return apiFetch(endpoint, { ...body, auth_user_id: authUserId });
}

/**
 * Admin API Ã§aÄŸrÄ±sÄ± â€” auth_user_id otomatik eklenir.
 */
async function adminFetch(endpoint, body = {}) {
  return authenticatedFetch(endpoint, body);
}

// ==========================================
// Auth API'leri
// ==========================================

/**
 * KullanÄ±cÄ± giriÅŸi.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>} - { id, businessId, businessName, username, fullName, role }
 */
export async function login(username, password) {
  return apiFetch("login.php", { username, password });
}

// ==========================================
// Ä°ÅŸletme (Business) API'leri
// ==========================================

/**
 * TÃ¼m iÅŸletmeleri getirir.
 * @returns {Promise<Array>}
 */
export async function getBusinesses() {
  return apiFetch("getBusinesses.php");
}

/**
 * Tek bir iÅŸletmeyi ID ile getirir.
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function getBusinessById(id) {
  return apiFetch("getBusinessById.php", { id });
}

// ==========================================
// Hizmet (Service) API'leri
// ==========================================

/**
 * Bir iÅŸletmeye ait hizmetleri getirir.
 * @param {number|string} businessId
 * @returns {Promise<Array>}
 */
export async function getServicesByBusiness(businessId) {
  return apiFetch("getServicesByBusiness.php", { businessId });
}

// ==========================================
// Ã‡alÄ±ÅŸan (Staff) API'leri
// ==========================================

/**
 * Bir iÅŸletmeye ait Ã§alÄ±ÅŸanlarÄ± getirir.
 * @param {number|string} businessId
 * @returns {Promise<Array>}
 */
export async function getStaffByBusiness(businessId) {
  return apiFetch("getStaffByBusiness.php", { businessId });
}

/**
 * Yeni Ã§alÄ±ÅŸan ekler (Admin-only).
 * @param {Object} staffData - { businessId, username, password, fullName, role }
 * @returns {Promise<Object>}
 */
export async function addStaff(staffData) {
  return adminFetch("addStaff.php", staffData);
}

/**
 * Ã‡alÄ±ÅŸan siler (Admin-only).
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function deleteStaff(id) {
  return adminFetch("deleteStaff.php", { id });
}

/**
 * Ã‡alÄ±ÅŸan Ã§alÄ±ÅŸma saatlerini gÃ¼nceller.
 * Ã‡alÄ±ÅŸan kendi saatlerini, admin herkesin saatlerini gÃ¼ncelleyebilir.
 * @param {number|string} staffId
 * @param {Object} schedule - { start, end, closedDays, maxBookingDays }
 * @returns {Promise<Object>}
 */
export async function updateStaffHours(staffId, schedule) {
  return adminFetch("updateStaffHours.php", {
    staffId,
    start: schedule.start,
    end: schedule.end,
    closedDays: schedule.closedDays ?? "",
    maxBookingDays: schedule.maxBookingDays,
  });
}

// ==========================================
// Randevu (Appointment) API'leri
// ==========================================

/**
 * Bir iÅŸletmeye ait randevularÄ± getirir.
 * @param {number|string} businessId
 * @param {string|null} date - Opsiyonel tarih filtresi (YYYY-MM-DD)
 * @param {number|string|null} staffId - Opsiyonel Ã§alÄ±ÅŸan filtresi
 * @returns {Promise<Array>}
 */
export async function getAppointmentsByBusiness(businessId, date = null, staffId = null) {
  const body = { businessId };
  if (date) body.date = date;
  if (staffId) body.staffId = staffId;
  return apiFetch("getAppointmentsByBusiness.php", body);
}

/**
 * Yeni randevu oluÅŸturur (Ã§akÄ±ÅŸma kontrolÃ¼ backend'de yapÄ±lÄ±r).
 * @param {Object} appointmentData
 * @returns {Promise<Object>}
 */
export async function createAppointment(appointmentData) {
  return apiFetch("createAppointment.php", appointmentData);
}

/**
 * Telefon numarasÄ± iÃ§in geÃ§ici doÄŸrulama kodu Ã¼retir ve saklar.
 * @param {string} phone
 * @returns {Promise<Object>}
 */
export async function requestPhoneVerificationCode(phone) {
  return apiFetch("requestPhoneVerificationCode.php", { phone });
}

/**
 * Telefon + kod doÄŸrulamasÄ± yapar.
 * EÅŸleÅŸme olursa geÃ§ici kod kaydÄ± backend'de silinir.
 * @param {string} phone
 * @param {string} code
 * @returns {Promise<Object>}
 */
export async function verifyPhoneVerificationCode(phone, code) {
  return apiFetch("verifyPhoneVerificationCode.php", { phone, code });
}

/**
 * Randevu durumunu gÃ¼nceller.
 * Admin kendi iÅŸletmesindeki tÃ¼m randevularÄ±, personel ise sadece kendi randevularÄ±nÄ± gÃ¼ncelleyebilir.
 * @param {number|string} id
 * @param {"pending"|"confirmed"|"cancelled"} status
 * @returns {Promise<Object>}
 */
export async function updateAppointmentStatus(id, status) {
  return authenticatedFetch("updateAppointmentStatus.php", { id, status });
}

// ==========================================
// Admin API'leri
// ==========================================

/**
 * Yeni hizmet ekler (Admin-only).
 * @param {Object} serviceData - { businessId, name, price, duration }
 * @returns {Promise<Object>}
 */
export async function addService(serviceData) {
  return adminFetch("addService.php", serviceData);
}

/**
 * Hizmeti gÃ¼nceller (Admin-only).
 * @param {number|string} id
 * @param {Object} serviceData - { name, price, duration }
 * @returns {Promise<Object>}
 */
export async function updateService(id, serviceData) {
  return adminFetch("updateService.php", { id, ...serviceData });
}

/**
 * Hizmeti siler (Admin-only).
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export async function deleteService(id) {
  return adminFetch("deleteService.php", { id });
}

/**
 * Ä°ÅŸletme mesai saatlerini gÃ¼nceller (Admin-only).
 * @param {number|string} businessId
 * @param {Object} hours - { start: "09:00", end: "18:00" }
 * @returns {Promise<Object>}
 */
export async function updateBusinessHours(businessId, hours) {
  return adminFetch("updateBusinessHours.php", {
    businessId,
    start: hours.start,
    end: hours.end,
  });
}

/**
 * Ä°ÅŸletme logosunu yÃ¼kler (Admin-only, multipart/form-data).
 * @param {number|string} businessId
 * @param {File} logoFile - PNG dosyasÄ±
 * @returns {Promise<Object>}
 */
export async function uploadBusinessLogo(businessId, logoFile) {
  const authUserId = getAuthUserId();
  if (!authUserId) {
    throw new Error("GiriÅŸ yapmanÄ±z gerekiyor.");
  }

  const formData = new FormData();
  formData.append("auth_user_id", String(authUserId));
  formData.append("businessId", String(businessId));
  formData.append("logoFile", logoFile);

  const response = await fetch(`${API_BASE}/uploadBusinessLogo.php`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || `HTTP ${response.status} hatasÄ±`);
  }

  return data.data;
}

/**
 * İletişim formu mesajını gönderir.
 * @param {Object} formData - { name, phone, email, message }
 * @returns {Promise<Object>}
 */
export async function sendContactEmail(formData) {
  return apiFetch("contact_mailer.php", formData);
}

/**
 * Belirli tarih aralığı için işletme raporlarını getirir (Admin-only).
 * @param {number|string} businessId
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @returns {Promise<Object>}
 */
export async function getReports(businessId, startDate, endDate) {
  return adminFetch("getReports.php", { businessId, startDate, endDate });
}
